import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, X, Crown, Zap, Calendar, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Subscription = () => {
  const { vendor } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payMode, setPayMode] = useState('auto'); // 'auto' = recurring auto-debit, 'once' = single payment

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('vendor_token');
      const response = await axios.get(`${API}/vendor/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscription(response.data);
    } catch (error) {
      toast.error('Failed to fetch subscription details');
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSelectPlan = async (planType) => {
    try {
      const token = localStorage.getItem('vendor_token');
      
      // Create Razorpay order
      const orderResponse = await axios.post(
        `${API}/vendor/subscription/create-order`,
        { plan_type: planType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { order_id, amount, currency, key_id } = orderResponse.data;
      
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        return;
      }
      
      // Configure Razorpay checkout
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'SAANJH',
        description: `${planType === 'monthly' ? 'Monthly' : 'Annual'} Subscription`,
        order_id: order_id,
        handler: async function (response) {
          try {
            // Verify payment on backend
            await axios.post(
              `${API}/vendor/subscription/verify-payment`,
              {
                order_id: response.razorpay_order_id,
                payment_id: response.razorpay_payment_id,
                signature: response.razorpay_signature
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success('Subscription activated successfully!');
            fetchSubscription(); // Refresh subscription data
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: vendor?.business_name || '',
          email: vendor?.email || ''
        },
        theme: {
          color: '#09090B'
        },
        modal: {
          ondismiss: function() {
            toast.info('Payment cancelled');
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment');
    }
  };

  // Recurring auto-debit: create a Razorpay subscription, authorize the mandate, then activate.
  const handleAutoPay = async (planType) => {
    const token = localStorage.getItem('vendor_token');
    let res;
    try {
      res = await axios.post(
        `${API}/vendor/subscription/subscribe`,
        { plan_type: planType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start auto-debit. Please try one-time payment.');
      return;
    }
    const { subscription_id, key_id } = res.data;
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) { toast.error('Failed to load payment gateway'); return; }
    const options = {
      key: key_id,
      subscription_id: subscription_id,
      name: 'SAANJH',
      description: `${planType === 'monthly' ? 'Monthly' : 'Annual'} auto-debit subscription`,
      handler: async function (response) {
        try {
          await axios.post(
            `${API}/vendor/subscription/verify-subscription`,
            {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success('Auto-debit subscription active — it will renew automatically.');
          fetchSubscription();
        } catch (error) {
          toast.error('Could not verify the subscription');
        }
      },
      prefill: { name: vendor?.business_name || '', email: vendor?.email || '' },
      theme: { color: '#09090B' },
      modal: { ondismiss: function () { toast.info('Setup cancelled'); } },
    };
    new window.Razorpay(options).open();
  };

  const handleCancelAutoPay = async () => {
    if (!window.confirm('Cancel auto-renew? Your plan stays active until the current period ends.')) return;
    try {
      const token = localStorage.getItem('vendor_token');
      const res = await axios.post(`${API}/vendor/subscription/cancel-autopay`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || 'Auto-renew cancelled');
      fetchSubscription();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not cancel auto-renew');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const sub = subscription?.subscription || {};
  const pricing = subscription?.pricing || {};

  return (
    <div data-testid="subscription-page">
      {/* Expired Subscription Alert */}
      {subscription?.subscription?.is_expired && (
        <Card className="mb-6 border-red-500 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="text-red-600 text-2xl">⚠️</div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-900 mb-2">
                  Your Subscription Has Expired
                </h3>
                <p className="text-red-800 mb-4">
                  Your store is currently inactive. Renew your subscription to continue accepting orders and managing your products.
                </p>
                <p className="text-sm text-red-700">
                  <strong>Limited Access:</strong> You can only view your subscription page until you renew.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="mb-8">
        <h2 className="text-3xl font-bold tracking-tight">Subscription</h2>
        <p className="text-muted-foreground mt-2">Manage your SAANJH subscription</p>
      </div>

      {/* Current Subscription Status */}
      <Card className="mb-8">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle>Current Subscription</CardTitle>
              <CardDescription>Your plan details and status</CardDescription>
            </div>
            <Badge
              variant={sub.status === 'active' ? 'default' : sub.status === 'trial' ? 'secondary' : 'destructive'}
              className="text-sm"
            >
              {sub.status === 'trial' ? '🎁 Free Trial' : sub.status === 'active' ? '✓ Active' : '⚠️ Expired'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Plan Type:</span>
              <span className="font-medium capitalize">{sub.plan_type?.replace('_', ' ')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Valid Until:</span>
              <span className="font-medium">
                {sub.valid_until ? new Date(sub.valid_until).toLocaleDateString() : 'N/A'}
              </span>
            </div>
            {sub.days_remaining > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Days Remaining:</span>
                <span className="font-bold text-primary">{sub.days_remaining} days</span>
              </div>
            )}
            {sub.auto_debit && !sub.cancel_scheduled && (
              <div className="flex items-center justify-between gap-3 p-3 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center gap-2 text-sm text-green-800">
                  <RefreshCw className="h-4 w-4" />
                  <span>Auto-renew is on — we'll charge automatically on {sub.valid_until ? new Date(sub.valid_until).toLocaleDateString() : 'renewal'}.</span>
                </div>
                <Button variant="outline" size="sm" onClick={handleCancelAutoPay} data-testid="cancel-autopay-btn">Cancel auto-renew</Button>
              </div>
            )}
            {sub.cancel_scheduled && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                Auto-renew is cancelled. Your plan stays active until {sub.valid_until ? new Date(sub.valid_until).toLocaleDateString() : 'the period ends'}, then it won't renew.
              </div>
            )}
            {sub.is_expired && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800 font-medium">
                  ⚠️ Your subscription has expired. Please renew to continue using SAANJH.
                </p>
              </div>
            )}
            
            {/* Subscription Features */}
            {!sub.is_expired && (
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-semibold mb-3">What's Included in Your Plan</h4>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>Unlimited products</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>Custom store theme & branding</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>QR code generation & download</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>Email notifications for orders</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>Custom pages with WYSIWYG editor</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>CSV bulk product upload</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payment mode toggle */}
      <div className="flex flex-col items-center mb-6">
        <div className="inline-flex rounded-lg border bg-muted p-1" data-testid="paymode-toggle">
          <button
            onClick={() => setPayMode('auto')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${payMode === 'auto' ? 'bg-background shadow text-foreground' : 'text-muted-foreground'}`}
          >
            <RefreshCw className="h-4 w-4" /> Auto-debit
            <span className="text-[10px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded">RECOMMENDED</span>
          </button>
          <button
            onClick={() => setPayMode('once')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${payMode === 'once' ? 'bg-background shadow text-foreground' : 'text-muted-foreground'}`}
          >
            One-time
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-center max-w-md">
          {payMode === 'auto'
            ? 'Set up once and your plan renews automatically — no missed renewals or store downtime. Cancel anytime.'
            : 'Pay for a single period. You\'ll need to renew manually before it expires.'}
        </p>
      </div>

      {/* Pricing Plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Monthly Plan */}
        <Card className="relative overflow-hidden border-2 hover:border-blue-500 transition-all">
          <div className="absolute top-0 right-0 bg-gradient-to-l from-blue-600 to-cyan-600 text-white px-4 py-1 text-xs font-bold rounded-bl-lg">
            POPULAR
          </div>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Zap className="h-6 w-6 text-blue-600" />
              </div>
              <CardTitle className="text-2xl">Monthly Plan</CardTitle>
            </div>
            <CardDescription>Perfect for getting started</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-6">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold">₹{pricing.monthly}</span>
                <span className="text-muted-foreground">/month</span>
              </div>
            </div>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-sm">Unlimited products</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-sm">Custom store theme</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-sm">QR code generation</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-sm">Email notifications</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-sm">Custom pages</span>
              </li>
              <li className="flex items-center gap-2 opacity-40">
                <X className="h-4 w-4 text-red-600 flex-shrink-0" />
                <span className="text-sm line-through">Priority support</span>
              </li>
              <li className="flex items-center gap-2 opacity-40">
                <X className="h-4 w-4 text-red-600 flex-shrink-0" />
                <span className="text-sm line-through">Advanced analytics</span>
              </li>
            </ul>
            <Button
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
              onClick={() => (payMode === 'auto' ? handleAutoPay('monthly') : handleSelectPlan('monthly'))}
              data-testid="select-monthly-btn"
            >
              {payMode === 'auto' ? (<><RefreshCw className="h-4 w-4 mr-2" />Auto-pay ₹{pricing.monthly}/mo</>) : `Pay ₹${pricing.monthly} once`}
            </Button>
          </CardContent>
        </Card>

        {/* Annual Plan */}
        <Card className="relative overflow-hidden border-2 border-primary hover:border-primary shadow-lg">
          <div className="absolute top-0 right-0 bg-gradient-to-l from-green-600 to-emerald-600 text-white px-4 py-1 text-xs font-bold rounded-bl-lg">
            BEST VALUE - Save ₹363/year
          </div>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg">
                <Crown className="h-6 w-6 text-white" />
              </div>
              <CardTitle className="text-2xl">Annual Plan</CardTitle>
            </div>
            <CardDescription>Best value for committed sellers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-6">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold">₹{pricing.annual}</span>
                <span className="text-muted-foreground">/year</span>
              </div>
              <p className="text-sm text-green-600 font-medium mt-1">
                Save ₹{pricing.monthly * 12 - pricing.annual} compared to monthly
              </p>
            </div>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-sm font-medium">Everything in Monthly</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-sm">Priority support</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-sm">2 months free</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-sm">Advanced analytics</span>
              </li>
            </ul>
            <Button
              className="w-full bg-primary hover:bg-primary/90"
              onClick={() => (payMode === 'auto' ? handleAutoPay('annual') : handleSelectPlan('annual'))}
              data-testid="select-annual-btn"
            >
              {payMode === 'auto' ? (<><RefreshCw className="h-4 w-4 mr-2" />Auto-pay ₹{pricing.annual}/yr</>) : (<><Calendar className="h-4 w-4 mr-2" />Pay ₹{pricing.annual} once</>)}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="mt-8 bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <div className="text-blue-600 mt-0.5">ℹ️</div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Secure Payments with Razorpay</h3>
              <p className="text-sm text-blue-800">
                Payments are processed securely by Razorpay (Cards, UPI, Net Banking, Wallets). With <strong>auto-debit</strong>
                {' '}you authorise once and your plan renews automatically (UPI AutoPay / card mandate) — cancel anytime. Prefer not to?
                {' '}Switch to <strong>one-time</strong> and renew manually each period.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Subscription;
